import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface QuotationMasterData {
  id: string
  organizationId: string
  // Numbering
  quotationPrefix: string
  quotationNumberSequence: number
  // Defaults
  defaultValidityDays: number
  defaultPaymentTerms: string
  defaultTermsAndConditions: string
  // Tax defaults
  defaultCgstRate: number
  defaultSgstRate: number
  defaultIgstRate: number
  // Settings
  requireCustomerGstin: boolean
  requireCustomerPan: boolean
  autoCalculateTaxes: boolean
  // Extended defaults
  defaultCurrency: string
  defaultGstEnabled: boolean
  defaultPlaceOfSupply: string
  defaultReverseCharge: boolean
  defaultDeclarationId: string | null
  defaultAssumptionIds: string[]
  defaultExclusionIds: string[]
  defaultTermIds: string[]
  defaultDisplaySettingId: string | null
  // Feature toggles
  assumptionsExclusionsEnabled: boolean
  // Document Numbering
  fiscalYear: string
  startingNumber: number
  currentCounter: number
  numberFormat: string
  padding: number
  createdAt: string
  updatedAt: string
}

export function useQuotationDefaults(filters?: any) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['quotationDefaults', organization?.id, filters],
    queryFn: async () => {
      const res = await api.get<any>(`/master-data/quotation-defaults?organizationId=${organization?.id || ''}`)
      if (!res?.data) return []
      const item = res.data
      const formatted = {
        ...item,
        id: item.id || item._id || 'default-profile',
        name: item.name || 'Organization Quotation Defaults',
        isActive: item.isActive !== false,
        defaultCurrency: item.defaultCurrency || 'INR',
        defaultValidityDays: item.defaultValidityDays || 30,
        defaultPaymentTermId: item.defaultPaymentTermId || '',
        defaultGstEnabled: item.defaultGstEnabled ?? true,
        defaultPlaceOfSupply: item.defaultPlaceOfSupply || '',
        defaultReverseCharge: item.defaultReverseCharge ?? false,
        defaultDeclarationId: item.defaultDeclarationId || '',
        defaultAssumptionsIds: item.defaultAssumptionIds || item.defaultAssumptionsIds || [],
        defaultExclusionsIds: item.defaultExclusionIds || item.defaultExclusionsIds || [],
        defaultTermsIds: item.defaultTermIds || item.defaultTermsIds || [],
        defaultDisplaySettingsId: item.defaultDisplaySettingId || item.defaultDisplaySettingsId || '',
        assumptionsExclusionsEnabled: item.assumptionsExclusionsEnabled ?? false,
      }

      // If search filter is provided, filter locally
      if (filters?.search && !formatted.name.toLowerCase().includes(filters.search.toLowerCase())) {
        return []
      }
      if (filters?.isActive !== undefined && formatted.isActive !== filters.isActive) {
        return []
      }

      return [formatted]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useQuotationDefaultMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['quotationDefaults', organization?.id] })
  }

  const mapPayload = (payload: any) => ({
    ...payload,
    defaultAssumptionIds: payload.defaultAssumptionIds || payload.defaultAssumptionsIds,
    defaultExclusionIds: payload.defaultExclusionIds || payload.defaultExclusionsIds,
    defaultTermIds: payload.defaultTermIds || payload.defaultTermsIds,
    defaultDisplaySettingId: payload.defaultDisplaySettingId || payload.defaultDisplaySettingsId,
  })

  const create = useMutation({
    mutationFn: (payload: any) =>
      api.post<any>('/master-data/quotation-defaults', mapPayload(payload), {
        organizationId: organization?.id,
        skipErrorToast: true,
      }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: any }) =>
      api.patch<any>(`/master-data/quotation-defaults?organizationId=${organization?.id}`, mapPayload(payload), {
        skipErrorToast: true,
      }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id?: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/quotation-defaults?organizationId=${organization?.id}`, { isActive }, {
        skipErrorToast: true,
      }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (_id: string) =>
      api.delete(`/master-data/quotation-defaults?organizationId=${organization?.id}`, {
        skipErrorToast: true,
      }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove }
}